import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, RefreshCw, Filter, Loader2, Send, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LogsPage() {
  const [searchParams] = useSearchParams();
  const ruleIdFromUrl = searchParams.get('rule_id');
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('payload'); // payload, url, error
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });

  useEffect(() => {
    fetchLogs();
  }, [statusFilter, pagination.page, searchQuery, searchType]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (ruleIdFromUrl) {
        params.rule_id = ruleIdFromUrl;
      }
      if (searchQuery) {
        params.search = searchQuery;
        params.search_type = searchType;
      }
      const response = await axios.get(`${API}/logs`, { params });
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Gagal mengambil logs');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (log) => {
    if (!log.destination_url) {
      toast.error('Tidak dapat resend: Tidak ada destination URL');
      return;
    }
    
    try {
      await axios.post(`${API}/logs/${log._id}/resend`);
      toast.success('Webhook berhasil di-queue untuk resending');
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Gagal resend webhook');
    }
  };

  const viewPayload = (log) => {
    setSelectedLog(log);
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: 'default',
      failed: 'destructive',
      queued: 'secondary',
      processing: 'outline'
    };
    return (
      <Badge variant={variants[status]} data-testid={`status-badge-${status}`}>
        {status}
      </Badge>
    );
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2" data-testid="logs-page-title">Webhook Logs</h1>
              <p className="text-gray-600">
                Monitor dan kelola webhook delivery attempts
                {ruleIdFromUrl && (
                  <Badge className="ml-2" variant="outline">
                    Filtered by rule
                  </Badge>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Input
                    placeholder="Search logs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-64 pr-8"
                    data-testid="search-input"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger className="w-32" data-testid="search-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payload">Payload</SelectItem>
                    <SelectItem value="url">URL</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Select value={statusFilter} onValueChange={(value) => {
                setStatusFilter(value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchLogs} variant="outline" data-testid="refresh-logs-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <Card data-testid="logs-table-card">
          <CardHeader>
            <CardTitle>Logs ({pagination.total})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                Loading...
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-gray-500" data-testid="no-logs-message">
                Tidak ada logs.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Status</TableHead>
                      <TableHead>Tanggal</TableHead>
                      <TableHead>Rule</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Retries</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log._id} data-testid={`log-row-${log._id}`}>
                        <TableCell>{getStatusBadge(log.status)}</TableCell>
                        <TableCell className="text-sm">
                          {new Date(log.created_at).toLocaleString('id-ID')}
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.rule_id?.name || '-'}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {log.forwarded_method || log.original_method}
                          </code>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 max-w-xs truncate">
                          {log.destination_url || 'N/A'}
                        </TableCell>
                        <TableCell>{log.retry_count}</TableCell>
                        <TableCell className="text-sm text-red-600 max-w-xs truncate">
                          {log.error_message || '-'}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewPayload(log)}
                            data-testid={`view-payload-btn-${log._id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {log.destination_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleResend(log)}
                              data-testid={`resend-btn-${log._id}`}
                              title="Resend webhook"
                            >
                              <Send className={`w-4 h-4 ${log.status === 'failed' ? 'text-red-500' : 'text-blue-500'}`} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-600">
                    Halaman {pagination.page} dari {pagination.pages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      data-testid="prev-page-btn"
                    >
                      Sebelumnya
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page === pagination.pages}
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      data-testid="next-page-btn"
                    >
                      Selanjutnya
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto" data-testid="payload-dialog">
          <DialogHeader>
            <DialogTitle>Webhook Payload</DialogTitle>
          </DialogHeader>
          <pre className="bg-gray-100 p-4 rounded-lg overflow-auto text-xs" data-testid="payload-content">
            {JSON.stringify(selectedLog?.payload, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
